// 12.TP1/#48: @sentry/react wird DYNAMISCH geladen — ~16KB gzip weniger
// im kritischen Entry-Pfad. Bis zum Laden gepufferte No-Op-Semantik.
type SentryModule = typeof import('@sentry/react');
let Sentry: SentryModule | null = null;

/**
 * Sentry-Init für die Sizilianer-Plattform.
 *
 * Wird in main.tsx aufgerufen BEVOR React rendert. Nur aktiv in Produktion und
 * wenn VITE_SENTRY_DSN gesetzt ist — sonst kompletter No-Op (kein Network, kein
 * Performance-Hit).
 *
 * Konfiguration:
 *   - VITE_SENTRY_DSN          → Project-DSN (Pflicht für Aktivierung)
 *   - VITE_SENTRY_ENVIRONMENT  → 'production' / 'preview' / 'development'
 *   - VITE_SENTRY_RELEASE      → Git-SHA o. Ä. (von Vercel gesetzt)
 *
 * Wir tracken:
 *   - Uncaught Errors / Promise-Rejections
 *   - React-ErrorBoundary-Catches (siehe ErrorBoundary.tsx → componentDidCatch)
 *   - Performance-Sample 10 % (Production)
 *
 * Filter:
 *   - DSGVO: Standard-PII off (Sentry default), wir senden bewusst keine E-Mails
 *     oder Namen mit
 *   - Localhost-Errors während Development bleiben in der Console
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    if (import.meta.env.PROD) {
      console.info('[sentry] VITE_SENTRY_DSN not set — error tracking disabled');
    }
    return;
  }

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    (import.meta.env.PROD ? 'production' : 'development');

  void import('@sentry/react').then((S) => {
    Sentry = S;
    S.init({
    dsn,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,

    // Performance-Sampling: 10 % in Prod, 100 % in Dev für Testing
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session Replay komplett aus — vermeidet DSGVO-Bedenken bei Restaurant-
    // Plattform mit User-Daten (Reservierungen, Mails)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // PII bewusst off
    sendDefaultPii: false,

    // Standard-Browser-Tracing für Page-Loads + Route-Wechsel
    integrations: [
      S.browserTracingIntegration(),
    ],

    // Filter: noise unterdrücken
    ignoreErrors: [
      // Browser-Extensions
      'top.GLOBALS',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network-Errors die nicht handlable sind
      'NetworkError',
      'Network request failed',
      'Load failed',
      // Cancelled fetch (User navigiert weg)
      'AbortError',
      // Bekannte Chrome-Extension-Noise
      'chrome-extension://',
    ],
    denyUrls: [
      // Browser-Extensions
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari-extension:\/\//,
    ],

    beforeSend(event, hint) {
      // In Dev: Console-Log statt Send
      if (!import.meta.env.PROD) {
        console.error('[sentry-dev]', event, hint);
        return null;
      }
      return event;
    },
    });
  });
}

/**
 * Manuelles Capture für strukturierte Errors mit zusätzlichem Context.
 * Nutze diesen Wrapper statt direkt Sentry.captureException — der Helper
 * setzt einheitliches Tagging (component, action) für bessere Filter im UI.
 */
export function captureError(
  error: unknown,
  context?: {
    component?: string;
    action?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extra?: Record<string, any>;
  },
) {
  if (!import.meta.env.PROD) {
    console.error(
      `[error${context?.component ? `:${context.component}` : ''}${context?.action ? `/${context.action}` : ''}]`,
      error,
      context?.extra,
    );
    return;
  }
  if (!Sentry) {
    // Sentry (noch) nicht geladen — nicht verlieren, wenigstens loggen
    console.error('[error:unreported]', error, context?.extra);
    return;
  }
  Sentry.withScope((scope) => {
    if (context?.component) scope.setTag('component', context.component);
    if (context?.action) scope.setTag('action', context.action);
    if (context?.extra) scope.setExtras(context.extra);
    Sentry!.captureException(error);
  });
}
